/**
 * T014: PostEditor Component
 *
 * Manual post editing interface with textarea and save/cancel controls
 */

import { memo, useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { Save, X, Loader2, Edit3 } from 'lucide-react';

interface IPostEditorProps {
  postId: string;
  initialContent: string;
  onSaveSuccess: (editedContent: string) => void;
  onCancel: () => void;
}

const PostEditor = ({ postId, initialContent, onSaveSuccess, onCancel }: IPostEditorProps) => {
  // ============================================
  // 1. HOOKS
  // ============================================
  const [editedText, setEditedText] = useState(() => initialContent);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const updatePostContent = useMutation(api.posts.updatePostContent);

  // ============================================
  // 2. HELPER FUNCTIONS
  // ============================================
  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedText(e.target.value);
    setError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editedText.trim()) {
      setError('Content cannot be empty');
      return;
    }

    if (editedText === initialContent) {
      setError('No changes detected');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await updatePostContent({
        postId: postId as Id<'posts'>,
        editedContent: editedText,
      });

      setIsSaving(false);
      onSaveSuccess(editedText);
    } catch (err) {
      setIsSaving(false);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
      setError(errorMessage);
    }
  }, [editedText, initialContent, postId, updatePostContent, onSaveSuccess]);

  const handleCancel = useCallback(() => {
    setEditedText(initialContent);
    setError(null);
    onCancel();
  }, [initialContent, onCancel]);

  // ============================================
  // 3. COMPUTED VALUES
  // ============================================
  const hasChanges = editedText !== initialContent;
  const canSave = hasChanges && !isSaving && editedText.trim().length > 0;
  const characterCount = editedText.length;
  const maxLength = 2000;
  const isNearLimit = characterCount > maxLength * 0.9;
  const isOverLimit = characterCount > maxLength;

  // ============================================
  // 4. SINGLE RETURN
  // ============================================
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Edit3 className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Manual Editing</h3>
      </div>

      {/* Textarea */}
      <div className="space-y-2">
        <Label htmlFor="post-content">Post Content</Label>
        <Textarea
          id="post-content"
          value={editedText}
          onChange={handleTextChange}
          className="min-h-[300px] font-sans text-base leading-relaxed"
          placeholder="Edit your post content and hashtags..."
          disabled={isSaving}
        />

        {/* Character Count */}
        <div
          className={`text-sm text-right ${
            isOverLimit
              ? 'text-destructive font-semibold'
              : isNearLimit
                ? 'text-warning font-medium'
                : 'text-muted-foreground'
          }`}
        >
          {characterCount} / {maxLength} characters
          {isOverLimit && ' (exceeds limit!)'}
        </div>
      </div>

      {/* Error Display */}
      {error && <ErrorMessage message={error} />}

      {/* Action Buttons */}
      <div className="flex gap-2 justify-end">
        <Button onClick={handleCancel} variant="outline" disabled={isSaving}>
          <X className="mr-2 h-4 w-4" />
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!canSave} variant="default">
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && !isSaving && (
        <div className="text-sm text-muted-foreground italic">You have unsaved changes</div>
      )}
    </div>
  );
};

export const PostEditorMemo = memo(PostEditor);
